import React from "react";
import { Logo } from "./Logo";
import { ProgressBar } from "./ProgressBar";

const STRIPE_URL =
  process.env.REACT_APP_STRIPE_CHECKOUT_URL ||
  "https://www.beambitious.chat/checkout";

const DEA_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_clarity-to-glow/artifacts/lrjuomfu_Gemini_Generated_Image_bnf1uxbnf1uxbnf1%20%282%29.png";

export const CompletionPage = ({ firstName }) => {
  return (
    <div
      className="relative min-h-screen bg-espresso text-cream py-20 sm:py-28 px-6 sm:px-8 overflow-hidden animate-glow-fade-up"
      data-testid="completion-page"
    >
      {/* Subtle gold accent glow */}
      <div
        className="pointer-events-none absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full opacity-20 blur-3xl"
        style={{ background: "radial-gradient(circle, #B8956A, transparent 60%)" }}
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full opacity-10 blur-3xl"
        style={{ background: "radial-gradient(circle, #B8956A, transparent 60%)" }}
        aria-hidden="true"
      />

      <div className="absolute top-6 left-6 md:top-10 md:left-12 z-20">
        <Logo dark />
      </div>

      <div className="relative max-w-[820px] mx-auto flex flex-col items-center text-center gap-14 pt-10 md:pt-16">
        <ProgressBar currentDay={5} tone="dark" />

        <div className="flex flex-col gap-6">
          <p
            className="font-body text-xs uppercase tracking-[0.32em] text-gold"
            data-testid="completion-eyebrow"
          >
            ✦ You made it ✦
          </p>
          <h1
            className="font-serif-editorial leading-[1.02] tracking-tight text-4xl sm:text-5xl lg:text-6xl text-cream"
            data-testid="completion-headline"
          >
            {firstName ? `${firstName}, y` : "Y"}ou just showed up for yourself{" "}
            <span className="italic text-gold">5 days in a row.</span>
          </h1>
          <p
            className="font-serif-editorial italic text-2xl sm:text-3xl text-gold"
            data-testid="completion-subheadline"
          >
            Do you know how rare that is?
          </p>
        </div>

        <div className="flex flex-col gap-4 max-w-[620px]" data-testid="completion-body">
          <p className="font-body text-[17px] leading-[1.85] text-cream/85">
            You got clear on what you want.
            <br />
            You identified the habits that will change everything.
            <br />
            You named the lie and replaced it with truth.
            <br />
            You built your first real plan.
            <br />
            You chose yourself.
          </p>
          <p className="font-serif-editorial italic text-xl text-gold">
            That's not nothing. That's everything.
          </p>
          <p className="font-body text-[17px] leading-[1.85] text-cream/85">
            Now imagine doing this every single day. Not just for 5 days — but
            for a lifetime. With a coach who knows your goals, remembers your
            journey, and shows up for you every morning before you even ask.
          </p>
          <p className="font-serif-editorial italic text-2xl text-cream pt-2">
            That's why I built <span className="text-gold">Dea.</span>
          </p>
        </div>

        {/* Dea intro card */}
        <div
          className="bg-cream text-espresso border-l-2 border-gold w-full max-w-[640px] text-left px-6 py-8 sm:px-10 sm:py-12"
          data-testid="dea-intro-card"
        >
          {/* Portrait — centered on mobile (block with auto margin),
              floats left on sm+ so the headline and body text wrap around it. */}
          <div
            className="relative overflow-hidden mx-auto mb-6 sm:mx-0 sm:mb-3 sm:float-left"
            style={{
              width: "170px",
              height: "215px",
              borderRadius: "4px",
              boxShadow: "0 12px 28px -14px rgba(61, 50, 41, 0.45)",
            }}
            data-testid="dea-portrait-wrapper"
          >
            <img
              src={DEA_PORTRAIT}
              alt="Dea — your AI glow up coach"
              className="absolute inset-0 w-full h-full object-cover object-top"
              loading="lazy"
              data-testid="dea-portrait"
            />
            <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gold" />
          </div>
          {/* Invisible spacer so floated image has right+bottom breathing room on sm+ */}
          <style>{`
            @media (min-width: 640px) {
              [data-testid="dea-portrait-wrapper"] {
                margin-right: 28px !important;
                margin-bottom: 12px !important;
              }
            }
          `}</style>

          <p className="font-serif-editorial italic text-3xl sm:text-4xl text-espresso leading-tight mb-1">
            Meet Dea.
          </p>
          <p className="font-body text-xs uppercase tracking-[0.28em] text-gold mb-5">
            Your AI Glow Up Coach
          </p>

          <p className="font-body text-[16px] leading-[1.75] text-espresso/85 mb-4">
            Dea is my AI glow up coach — trained on everything I know about
            clarity, habits, mindset, strategy, and well-being.
          </p>
          <p className="font-body text-[16px] leading-[1.75] text-espresso/85 mb-4">
            She builds your personalized 90-day glow up plan in your first
            conversation. She checks in with you every single day. She holds
            you accountable with love, pushes you when you need it, and
            celebrates every win along the way.
          </p>
          <p className="font-body text-[16px] leading-[1.75] text-espresso/85">
            She's everything you just experienced this week —{" "}
            <span className="italic font-medium text-espresso">
              every single day. For life.
            </span>
          </p>

          {/* Clear the float so the card's bottom edge sits correctly */}
          <div className="clear-both" aria-hidden="true" />
        </div>

        {/* Offer box */}
        <div
          className="w-full max-w-[640px] bg-gold text-espresso px-8 py-12 sm:px-12 sm:py-14 flex flex-col gap-7 items-center text-center"
          data-testid="offer-box"
        >
          <div className="flex flex-col gap-2">
            <p className="font-body uppercase text-xs tracking-[0.32em] text-espresso/70">
              The Glow Up Collective
            </p>
            <p className="font-serif-editorial text-3xl sm:text-4xl text-espresso leading-tight">
              3.0 Founding Member Access
            </p>
          </div>

          <div className="flex items-baseline gap-2">
            <span className="font-serif-editorial text-5xl sm:text-6xl text-espresso font-medium">
              $97
            </span>
            <span className="font-body text-sm uppercase tracking-[0.24em] text-espresso/70">
              one time · lifetime
            </span>
          </div>

          <ul
            className="flex flex-col gap-3 text-left w-full max-w-[320px]"
            data-testid="offer-bullets"
          >
            {[
              "Lifetime access to Dea",
              "Your HER Blueprint",
              "Daily check-ins + accountability",
              "Founding Member Broadcast Channel",
            ].map((line, i) => (
              <li
                key={i}
                className="font-body text-[15px] text-espresso flex items-start gap-3"
              >
                <span className="text-espresso mt-[2px]">✓</span>
                <span>{line}</span>
              </li>
            ))}
          </ul>

          <a
            href={STRIPE_URL}
            className="btn-dark-large w-full sm:w-auto"
            data-testid="join-collective-btn"
          >
            I'm Ready — Join For $97 →
          </a>

          <p className="font-body text-xs text-espresso/70 max-w-xs">
            Limited founding member pricing. Once spots close, this rate
            retires.
          </p>
        </div>

        <div className="flex flex-col gap-2 items-center pt-4" data-testid="not-ready-block">
          <p className="font-body text-sm text-cream/60">
            Not ready yet? That's okay.
          </p>
          <a
            href={process.env.REACT_APP_MAIN_SITE_URL || "https://beambitious.chat"}
            className="font-body text-sm text-gold underline-offset-4 hover:underline transition"
            data-testid="back-to-main-link"
          >
            Go back to beambitious.chat
          </a>
        </div>
      </div>
    </div>
  );
};
